import { EnumValues } from '@/services/types/EnumValues';
import { BusinessAccessLevelEnum } from '@/services/types/Account';

/**
 * A team member's account status, as returned by GET /businesses/{businessId}/members
 * (IGetTeamMembersResponse.accountStatus). Deliberately separate from Account.ts's
 * AccountStatusEnum - that one models a *personal account's* KYC/verification status
 * ('pending_rfi' etc.), a different concept that happens to share some label text.
 */
export const MemberAccountStatusEnum = {
  ACTIVE: 'active',
  PENDING: 'pending',
  PAUSED: 'paused',
  INACTIVE: 'inactive'
} as const;

export type TMemberAccountStatus = EnumValues<typeof MemberAccountStatusEnum>;

export type TAccessLevel = EnumValues<typeof BusinessAccessLevelEnum>;

/**
 * One row of the Team Members table - IGetTeamMembersResponse reshaped for display, with card
 * counts folded in (see useTeamMembers.ts for how those are derived - there is no card-count
 * field on the API today, see buildTeamMemberRows.ts).
 */
export type TTeamMemberRow = {
  /** A real member's customerId, or `invite:${pendingInvitationId}` for a not-yet-accepted invite
   *  (which has no customerId of its own yet) - see buildPendingInviteRows. Only ever used as a
   *  table-row/selection key; every mutation on a pending row goes through pendingInvitationId,
   *  never this. */
  customerId: string;
  /** Preferred name if set, else "firstName lastName" - supersedes the full name per Figma. */
  displayName: string;
  /** The full legal name, shown as a secondary line only when displayName is a preferred name. */
  legalName?: string;
  phoneDisplay: string;
  email: string;
  accessLevel: TAccessLevel;
  /** Always PENDING for a not-yet-accepted invite row - reuses the same status/badge the table
   *  already renders for a member whose own account is pending, rather than a parallel concept. */
  accountStatus: TMemberAccountStatus;
  /** Set only once this member has uploaded a photo - see useTeamMemberPhoto. */
  displayPictureId?: string | null;
  activeCardCount: number;
  frozenCardCount: number;
  /** Precomputed lowercase "name phone email" blob, so search doesn't re-derive it per keystroke. */
  searchText: string;
  /** Set only for a not-yet-accepted invite row (see buildPendingInviteRows) - the id to use
   *  against the pending-invitations endpoints, and the signal that this row has no real
   *  customerId yet: it can't be selected for any bulk member action (pause/freeze/remove/update
   *  access all need a real member), and clicking it opens the invite editor instead of the
   *  member details drawer. */
  pendingInvitationId?: string;
};
